import React, { useEffect } from 'react';
import { ArrowLeft, ArrowRight, IndianRupee, Clock, Calendar } from 'lucide-react';

// UI Components
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle, 
  CardDescription,
  CardFooter,
  Button,
  SelectionCard,

} from '../../../components/ui';

// Store & Constants
import { useFormStore } from '../../../store';
import { 
  BUDGET_OPTIONS, 
  BUDGET_FLEXIBILITY_OPTIONS,
  URGENCY_OPTIONS,
  EXPECTED_USAGE_YEARS_OPTIONS,
  FUTURE_ACTIVITIES,
} from '../../../constants';

// Types
import type { BudgetRange, BudgetFlexibility, Urgency, ExpectedUsageYears } from '../../../types';
import styles from './Steps.module.css';

export function FuturePlansStep() {

  const { formData, updateFuturePlans, nextStep, prevStep } = useFormStore();
  const { futurePlans } = formData;

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Handlers
  const handleBudget = (value: string) => {
    updateFuturePlans({ budgetRange: value as BudgetRange });
  };

  const handleBudgetFlexibility = (value: string) => {
    updateFuturePlans({ budgetFlexibility: value as BudgetFlexibility });
  };

  const handleUrgency = (value: string) => {
    updateFuturePlans({ urgency: value as Urgency });
  };

  const handleExpectedYears = (value: string) => {
    updateFuturePlans({ expectedUsageYears: value as ExpectedUsageYears });
  };

  const handleActivity = (activity: string) => {
    const current = futurePlans.plannedActivities || [];
    const updated = current.includes(activity)
      ? current.filter(a => a !== activity)
      : [...current, activity];
    
    updateFuturePlans({ plannedActivities: updated });
  };

  const handleNotes = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    updateFuturePlans({ additionalNotes: e.target.value });
  };

  const handleNextStep = () => {
    // if (!futurePlans.budgetRange) {
    //   setToast('Please select your budget range.');
    //   return;
    // }
    // if (!futurePlans.urgency) {
    //   setToast('Please select when you need the device.');
    //   return;
    // }
    nextStep();
  };

  return (
    <>

      
      <Card variant="elevated">
        <CardHeader>
          <CardTitle>Budget & Future Plans</CardTitle>
          <CardDescription>
            Tell us your budget and goals so we recommend something future-proof.
          </CardDescription>
        </CardHeader>
        
        <CardContent className={styles.formContainer}>
          {/* Budget Range */}
          <div className={styles.sectionTitle}>
            <h4>
              <IndianRupee size={18} className={styles.inlineIcon} />
              What's your budget range?
            </h4>
          </div>
          <div className={styles.optionsGrid}>
            {BUDGET_OPTIONS.map(option => (
              <SelectionCard
                key={option.value}
                selected={futurePlans.budgetRange === option.value}
                onClick={() => handleBudget(option.value)}
                title={option.label}
                size="compact"
              />
            ))}
          </div>

          {/* Budget Flexibility */}
          <div className={styles.sectionTitle}>
            <h4>How flexible is your budget?</h4>
          </div>
          <div className={styles.optionsGrid}>
            {BUDGET_FLEXIBILITY_OPTIONS.map(option => (
              <SelectionCard
                key={option.value}
                selected={futurePlans.budgetFlexibility === option.value}
                onClick={() => handleBudgetFlexibility(option.value)}
                title={option.label}
                description={option.description}
                size="compact"
              />
            ))}
          </div>

          {/* Urgency */}
          <div className={styles.sectionTitle}>
            <h4>
              <Clock size={18} className={styles.inlineIcon} />
              When do you need this?
            </h4>
          </div>
          <div className={styles.optionsGrid}>
            {URGENCY_OPTIONS.map(option => (
              <SelectionCard
                key={option.value}
                selected={futurePlans.urgency === option.value}
                onClick={() => handleUrgency(option.value)}
                title={option.label}
                description={option.description}
                size="compact"
              />
            ))}
          </div>

          {/* Expected Usage Years */}
          <div className={styles.sectionTitle}>
            <h4>
              <Calendar size={18} className={styles.inlineIcon} />
              How long do you expect to use this device?
            </h4>
          </div>
          <div className={styles.optionsGrid}>
            {EXPECTED_USAGE_YEARS_OPTIONS.map(option => (
              <SelectionCard
                key={option.value}
                selected={futurePlans.expectedUsageYears === option.value}
                onClick={() => handleExpectedYears(option.value)}
                title={option.label}
                description={option.description}
                size="compact"
              />
            ))}
          </div>

          {/* Future Activities */}
          <div className={styles.sectionTitle}>
            <h4>Any future activities you're planning?</h4>
            <p className={styles.sectionSubtitle}>Select all that you might start in the next 2 years</p>
          </div>
          <div className={styles.optionsRow}>
            {FUTURE_ACTIVITIES.map(activity => (
              <button
                key={activity}
                type="button"
                className={`${styles.chipButton} ${futurePlans.plannedActivities?.includes(activity) ? styles.chipActive : ''}`}
                onClick={() => handleActivity(activity)}
              >
                {activity}
              </button>
            ))}
          </div>

          {/* Additional Notes */}
          <div className={styles.sectionTitle}>
            <h4>Anything else we should know?</h4>
            <p className={styles.sectionSubtitle}>Any special requirements or preferences</p>
          </div>
          <textarea
            className={styles.textarea}
            placeholder="E.g., need specific ports, prefer matte display, will use external monitor..."
            value={futurePlans.additionalNotes}
            onChange={handleNotes}
            rows={3}
          />
        </CardContent>

        <CardFooter className={styles.footerSpaceBetween}>
          <Button variant="outline" onClick={prevStep}>
            <ArrowLeft size={18} />
            Back
          </Button>
          <Button onClick={handleNextStep} size="lg">
            Review & Pay
            <ArrowRight size={18} />
          </Button>
        </CardFooter>
      </Card>
    </>
  );
}